// FormVehicleType.tsx
import React from 'react';
import { useSelector } from 'react-redux';
import UseGetVehicleType from '../../hooks/vehicleType/useGetVehicleType';
import UseRegisterVehicleType from '../../hooks/vehicleType/useRegisterVehicleType';
import UseUpdateVehicleType from '../../hooks/vehicleType/useUpdateVehicleType';
import { ApplicationState } from '../../store';
import ActivityIndicator from '../ActivityIndicator';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import ButtonComponent from '../atoms/button';
import validationSchema, { VehicleFormType } from './validations';
import { yupResolver } from '@hookform/resolvers/yup';
import ControllerInput from '../atoms/controller-input';
import colors from '../../assets/styles/colors';
import formatNumberToView from '../../utils/formatNumber';
import Typography from '../atoms/typography';

const useStyles = makeStyles(theme => ({
  resultText: {
    fontFamily: 'Quicksand, sans-serif',
    fontWeight: 500,
    fontSize: '9',
    color: colors.primary.orange,
  },
}));

const FormVehicleType: React.FC = () => {
  const vehicleType = useSelector(
    (state: ApplicationState) => state.vehicleType,
  );
  const { execute, loading } = UseRegisterVehicleType();
  const { execute: executeUpdate, loading: loadingUpdate } =
    UseUpdateVehicleType();
  const { loading: loadingGetVehicleType } = UseGetVehicleType();
  const history = useHistory();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, defaultValues },
    watch,
  } = useForm<VehicleFormType>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      vehicleType: vehicleType.vehicleType || '',
      costPerAssistant: Number(vehicleType.costPerAssistant) || undefined,
      driverCost: Number(vehicleType.driverCost) || undefined,
      costPerKilometer: Number(vehicleType.costPerKilometer) || undefined,
      costPerTon: Number(vehicleType.costPerTon) || undefined,
      payloadCapacity: Number(vehicleType.payloadCapacity) / 100 || undefined,
      costPerOrder: Number(vehicleType.costPerOrder) || undefined,
      packLoadWeight: Number(vehicleType.packLoadWeight) / 100 || undefined,
    },
  });

  const watchPayloadCapacity = watch('payloadCapacity');
  const watchPackLoadWeight = watch('packLoadWeight');
  const isEdit = Boolean(vehicleType.vehicleTypeId);

  const onSubmit = async (data: VehicleFormType) => {
    if (isEdit) {
      if (JSON.stringify(defaultValues) !== JSON.stringify(data)) {
        await executeUpdate(data, vehicleType.vehicleTypeId);
      }
    } else {
      await execute(data);
    }
  };

  if (loading || loadingUpdate || loadingGetVehicleType) {
    return <ActivityIndicator />;
  }

  return (
    <>
      <Box>
        <ControllerInput
          name="vehicleType"
          control={control}
          disabled={isEdit}
          type="default"
          label="Tipo de Veículo (obrigatório)"
          placeholder="Insira o tipo de veículo"
          errorMessage={errors.vehicleType?.message}
        />

        <br />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControllerInput
              name="costPerAssistant"
              control={control}
              type="number"
              label="Custo por ajudante (obrigatório)"
              placeholder="00.00"
              errorMessage={errors.costPerAssistant?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$/ajudante
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ControllerInput
              name="driverCost"
              control={control}
              type="number"
              label="Custo do motorista (obrigatório)"
              placeholder="00.00"
              errorMessage={errors.driverCost?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControllerInput
              name="costPerKilometer"
              control={control}
              type="number"
              label="Custo por Kilometragem (obrigatório)"
              placeholder="00.00"
              errorMessage={errors.costPerKilometer?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$/Km
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ControllerInput
              name="costPerTon"
              control={control}
              type="number"
              label="Custo por tonelada (obrigatório)"
              placeholder="00.00"
              errorMessage={errors.costPerTon?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$/tn
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControllerInput
              name="costPerCubicMeter"
              control={control}
              type="number"
              label="Custo por metro cúbico"
              placeholder="00.00"
              errorMessage={errors.costPerCubicMeter?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$/m²
                  </Typography>
                </Box>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <ControllerInput
              name="costPerOrder"
              control={control}
              type="number"
              label="Custo por pedido (obrigatório)"
              placeholder="00.00"
              errorMessage={errors.costPerOrder?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    R$/pedido
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControllerInput
              name="payloadCapacity"
              control={control}
              type="number"
              label="Capacidade de carga (obrigatório)"
              placeholder="0"
              maxLength={3}
              errorMessage={errors.payloadCapacity?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    toneladas
                  </Typography>
                </Box>
              }
            />
            {watchPayloadCapacity &&
            watchPayloadCapacity !== 0 &&
            !errors.payloadCapacity ? (
              <Box marginTop={0.5}>
                <Typography fontSize="xxs" color="primary">
                  Valor em kilos:{' '}
                  <b>{formatNumberToView(watchPayloadCapacity * 100)} KG </b>
                </Typography>
              </Box>
            ) : null}
          </Grid>
          <Grid item xs={6}>
            <ControllerInput
              name="packLoadWeight"
              control={control}
              type="number"
              label="Peso da embalagem (obrigatório)"
              placeholder="0"
              maxLength={3}
              errorMessage={errors.packLoadWeight?.message}
              startAdornment={
                <Box marginRight={0.5}>
                  <Typography fontSize="xxs" color="primary">
                    toneladas
                  </Typography>
                </Box>
              }
            />
            {watchPackLoadWeight &&
            watchPackLoadWeight !== 0 &&
            !errors.packLoadWeight ? (
              <Box marginTop={0.5}>
                <Typography fontSize="xxs" color="primary">
                  Valor em kilos:{' '}
                  <b>{formatNumberToView(watchPackLoadWeight * 100)} KG </b>
                </Typography>
              </Box>
            ) : null}
          </Grid>
        </Grid>
        <Row
          className="container-buttons justify-content-between mt-4"
          noGutters
        >
          <ButtonComponent
            theme="outline-primary"
            onClick={() => history.goBack()}
          >
            Cancelar
          </ButtonComponent>
          <ButtonComponent
            disabled={!isValid && !isEdit}
            onClick={handleSubmit(onSubmit)}
          >
            {isEdit ? 'Editar' : 'Salvar'}
          </ButtonComponent>
        </Row>
      </Box>
    </>
  );
};

export default FormVehicleType;
